/**
 * Language Store
 *
 * Manages language selection and available languages state.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Language, LanguageCode, LanguageProgress } from '../services/languageApi.js';
import {
  fetchAvailableLanguages,
  fetchUserLanguagePreference,
  updateUserLanguage,
  fetchUserLanguageProgress,
} from '../services/languageApi.js';
import { debounceAsync } from '../utils/debounce.js';

interface LanguageState {
  // State
  availableLanguages: Language[];
  currentLanguage: LanguageCode | null;
  languageProgress: { [languageCode: string]: LanguageProgress };
  languagesStudied: LanguageCode[];
  isLoading: boolean;
  error: string | null;
  lastLanguagesFetch: number | null; // Timestamp of last language fetch

  // Actions
  loadAvailableLanguages: (forceRefresh?: boolean) => Promise<void>;
  loadUserLanguagePreference: (userId: string) => Promise<void>;
  setUserLanguage: (userId: string, languageCode: LanguageCode) => Promise<void>;
  loadUserLanguageProgress: (userId: string) => Promise<void>;
  setCurrentLanguage: (languageCode: LanguageCode) => void;
  clearLanguageData: () => void;
}

// Cache duration: 1 hour (languages rarely change)
const LANGUAGE_CACHE_DURATION = 60 * 60 * 1000;

// Debounce delay for language switching (500ms to prevent rapid switches)
const LANGUAGE_SWITCH_DEBOUNCE = 500;

// Create debounced version of updateUserLanguage to prevent race conditions
const debouncedUpdateUserLanguage = debounceAsync(updateUserLanguage, LANGUAGE_SWITCH_DEBOUNCE);

/**
 * Language store - manages multi-language support
 * Persisted to localStorage for current language preference
 */
export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      // Initial state
      availableLanguages: [],
      currentLanguage: null,
      languageProgress: {},
      languagesStudied: [],
      isLoading: false,
      error: null,
      lastLanguagesFetch: null,

      // Load all available languages with caching
      loadAvailableLanguages: async (forceRefresh = false) => {
        const state = get();

        // Check if cache is still valid
        if (!forceRefresh && state.lastLanguagesFetch && state.availableLanguages.length > 0) {
          const timeSinceLastFetch = Date.now() - state.lastLanguagesFetch;
          if (timeSinceLastFetch < LANGUAGE_CACHE_DURATION) {
            // Cache is still valid, return cached data
            return;
          }
        }

        set({ isLoading: true, error: null });
        try {
          const languages = await fetchAvailableLanguages();
          set({
            availableLanguages: languages,
            lastLanguagesFetch: Date.now(),
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load languages',
            isLoading: false,
          });
          throw error;
        }
      },

      // Load user's language preference
      loadUserLanguagePreference: async (userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const preference = await fetchUserLanguagePreference(userId);
          set({
            currentLanguage: preference.targetLanguage,
            languagesStudied: preference.languagesStudied,
            languageProgress: {
              [preference.targetLanguage]: preference.currentProgress,
            },
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load language preference',
            isLoading: false,
          });
          throw error;
        }
      },

      // Set user's target language (debounced to prevent rapid switches)
      setUserLanguage: async (userId: string, languageCode: LanguageCode) => {
        set({ isLoading: true, error: null });
        try {
          // Use debounced version to prevent race conditions from rapid switching
          const result = await debouncedUpdateUserLanguage(userId, languageCode);
          set({
            currentLanguage: result.targetLanguage,
            languageProgress: {
              ...get().languageProgress,
              [result.targetLanguage]: result.progress,
            },
            isLoading: false,
          });

          // Reload user language preference to get updated languages studied
          await get().loadUserLanguagePreference(userId);
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to set language',
            isLoading: false,
          });
          throw error;
        }
      },

      // Load all user language progress
      loadUserLanguageProgress: async (userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const progress = await fetchUserLanguageProgress(userId);
          set({ languageProgress: progress, isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load language progress',
            isLoading: false,
          });
          throw error;
        }
      },

      // Set current language locally (without API call)
      setCurrentLanguage: (languageCode: LanguageCode) => {
        set({ currentLanguage: languageCode });
      },

      // Clear all language data
      clearLanguageData: () => {
        set({
          currentLanguage: null,
          languageProgress: {},
          languagesStudied: [],
          error: null,
        });
      },
    }),
    {
      name: 'language-storage',
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
        availableLanguages: state.availableLanguages,
        lastLanguagesFetch: state.lastLanguagesFetch,
      }),
    }
  )
);
